<template>
  <div class="br-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <transition name="toast">
      <div
        v-if="baseToast.message"
        class="br-toast"
        :class="'is-' + (baseToast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ baseToast.message }}</span>
      </div>
    </transition>

    <transition v-if="isPermission" name="fade" mode="out-in">
      <!-- ==================== Form view ==================== -->
      <section key="form" v-if="isCreateUser" class="form-view">
        <div class="form-head">
          <button
            type="button"
            class="back-link"
            :disabled="isLoading"
            @click="isCreateUser = false"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>Branches</span>
          </button>
          <span class="head-sep">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span
            class="head-chip"
            :class="mode === 'create' ? 'head-chip-new' : 'head-chip-edit'"
          >{{ mode === 'create' ? 'New' : 'Editing' }}</span>
          <span v-if="mode === 'update'" class="head-name">
            {{ selectedUser.name || selectedUser.username }}
            <span v-if="selectedUser.username" class="head-name-sub">@{{ selectedUser.username }}</span>
          </span>
          <span v-else class="head-name">Branch</span>
        </div>

        <div v-if="isLoading" class="loading-block">
          <div class="loading-spinner"></div>
          <div class="loading-label">{{ mode === 'create' ? 'Creating…' : 'Updating…' }}</div>
        </div>
        <user-form
          v-else
          :mode="mode"
          :selected-user="selectedUser"
          @create-user="createUser"
        ></user-form>
      </section>

      <!-- ==================== List view ==================== -->
      <section key="list" v-else class="list-view">
        <header class="br-header">
          <div>
            <div class="eyebrow">Vendor</div>
            <h1>Branches</h1>
            <p class="subtitle">Manage branches, commission rates, opening balances, and routing exclusivity.</p>
          </div>
          <div class="header-actions">
            <button
              v-if="can('branch.create')"
              class="btn btn-primary"
              type="button"
              @click.stop="createBtnClicked"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
                <path d="M12 5v14M5 12h14" stroke-linecap="round" />
              </svg>
              New Branch
            </button>
          </div>
        </header>

        <!-- Filters -->
        <section class="br-filters">
          <div class="search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
            </svg>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search by username, name, email, phone, UPI ID…"
              class="search-input"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="search-clear"
              @click="searchQuery = ''"
              title="Clear"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <div class="chip-group">
            <button
              v-for="opt in statusOptions"
              :key="opt.value"
              type="button"
              class="chip"
              :class="{ 'chip-active': statusFilter === opt.value }"
              @click="statusFilter = opt.value"
            >{{ opt.label }}</button>
          </div>
        </section>

        <!-- Body -->
        <div v-if="isLoading" class="br-loading">
          <div class="loading-spinner"></div>
          <div class="loading-label">Loading branches…</div>
        </div>

        <div v-else-if="!hasFilteredUsers" class="empty-hero">
          <div class="empty-hero-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="empty-hero-title">No branches found</div>
          <div class="empty-hero-sub">
            {{ searchQuery ? 'Try clearing your search or filters.' : 'Create your first branch to get started.' }}
          </div>
        </div>

        <div v-else class="br-body">
          <transition-group
            tag="div"
            name="branch-list"
            class="br-grid"
          >
            <article
              v-for="branch in pagedUsers"
              :key="branch.username"
              class="branch-card"
              @click="editUser(branch)"
            >
              <div class="card-top">
                <div class="avatar">{{ initialsOf(branch) }}</div>
                <div class="branch-meta">
                  <div class="branch-name">{{ branch.name || branch.username }}</div>
                  <div class="branch-sub">
                    <span>@{{ branch.username }}</span>
                    <span v-if="branch.email" class="dot-sep">·</span>
                    <span v-if="branch.email" class="branch-email">{{ branch.email }}</span>
                  </div>
                </div>
                <div class="card-badges">
                  <span
                    v-if="branchExclusiveLabel(branch)"
                    class="pill pill-danger"
                    :title="`For the ${branchExclusiveLabel(branch).toLowerCase()} direction, this branch only serves clients in a routing group that includes it.`"
                  >Exclusive · {{ branchExclusiveLabel(branch) }}</span>
                  <span
                    class="pill"
                    :class="branch.status ? 'pill-success' : 'pill-neutral'"
                  >{{ branch.status ? 'Active' : 'Inactive' }}</span>
                </div>
              </div>

              <div v-if="isLoadingStats" class="card-stats card-stats-loading">
                <div class="skeleton"></div>
                <div class="skeleton"></div>
                <div class="skeleton"></div>
              </div>
              <div v-else class="card-stats">
                <div class="stat-cell">
                  <div class="stat-label">Pay-in today</div>
                  <div class="stat-value">{{ formatMoney(getStat(branch, 'payInApprovedAmount')) }}</div>
                  <div class="stat-sub">{{ getStat(branch, 'payInApprovedCount') || 0 }} orders</div>
                </div>
                <div class="stat-cell">
                  <div class="stat-label">Pay-out today</div>
                  <div class="stat-value">{{ formatMoney(getStat(branch, 'payOutApprovedAmount')) }}</div>
                  <div class="stat-sub">{{ getStat(branch, 'payOutApprovedCount') || 0 }} orders</div>
                </div>
                <div class="stat-cell">
                  <div class="stat-label">Pay-in limit left</div>
                  <div class="stat-value">{{ formatMoney(branch.payInLimit) }}</div>
                  <div class="stat-sub">today's remaining</div>
                </div>
              </div>

              <div class="card-actions" @click.stop>
                <button
                  type="button"
                  class="action-btn action-report"
                  @click.stop="openBranchReport(branch)"
                  title="View branch report"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 3v18h18" stroke-linecap="round" />
                    <path d="M7 15l4-4 4 4 5-5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Report
                </button>
                <div
                  class="action-toggle"
                  :class="{ 'action-toggle-updating': updatingStatuses[branch.username] }"
                >
                  <span class="toggle-label">Status</span>
                  <base-toggle
                    :key="`toggle-${branch.username}-${toggleVersions[branch.username] || 0}`"
                    class="mb-0"
                    :is-checked="branch.status === 1"
                    @rsd-checkbox-changed="toggleStatus(branch)"
                  ></base-toggle>
                  <div
                    v-if="updatingStatuses[branch.username]"
                    class="mini-spinner"
                  ></div>
                </div>
                <button
                  v-if="can('branch.edit')"
                  type="button"
                  class="action-btn action-edit"
                  @click.stop="editUser(branch)"
                  title="Edit branch"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 20h9" stroke-linecap="round" />
                    <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Edit
                </button>
                <button
                  v-if="can('branch.delete')"
                  type="button"
                  class="action-btn action-delete"
                  @click.stop="deleteUser(branch)"
                  title="Delete branch"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
            </article>
          </transition-group>

          <div class="pagination-wrap">
            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
          </div>
        </div>

        <teleport to="body">
          <transition name="modal-fade">
            <div v-if="openBranchStatistics" class="modal-backdrop" @click.self="closeBranchReport">
              <div class="modal-card modal-card-wide">
                <div class="modal-head">
                  <div>
                    <div class="modal-eyebrow">Branch report</div>
                    <div class="modal-title">{{ selectedBranchUsername }}</div>
                    <div class="modal-sub">Pay-in and pay-out activity across all statuses.</div>
                  </div>
                  <button type="button" class="modal-close" @click="closeBranchReport">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                      <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                    </svg>
                  </button>
                </div>
                <div class="modal-body modal-body-scroll">
                  <branch-report :branch-username="selectedBranchUsername"></branch-report>
                </div>
              </div>
            </div>
          </transition>
        </teleport>
      </section>
    </transition>

    <!-- Permission fallback -->
    <div v-else class="permission-block">
      <div class="permission-card">
        <div class="permission-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" />
          </svg>
        </div>
        <h2>You don't have permission to access this page</h2>
        <p>Head back to the dashboard.</p>
        <router-link to="/" class="btn btn-primary">Go to Home</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import UserForm from "../../components/sub-admin/UserForm.vue";
import BranchReport from "../../components/branches/BranchReport.vue";
import Pagination from "../../components/shared/pagination.vue";
import http from "../../http-common";
import moment from "moment-timezone";

export default {
  name: "branches",
  components: { UserForm, BranchReport, Pagination },
  data() {
    return {
      isPermission: true,
      isLoading: false,
      isLoadingStats: false,
      error: null,
      isCreateUser: false,
      baseToast: { type: "", message: "" },
      mode: "create",
      selectedUser: Object,
      // Always compute "today" in the configured display TZ (IST), not the
      // browser local TZ. Otherwise the stats date rolls over at UTC midnight
      // (5:30 AM IST) instead of at IST midnight.
      statsDate: moment().tz(process.env.VUE_APP_TIMEZONE || "Asia/Kolkata").format("YYYY-MM-DD"),
      branchStats: {},
      openBranchStatistics: false,
      selectedBranchUsername: "",
      // Per-branch toggle re-render counters. Bumped after a status-change
      // dialog is cancelled so the <base-toggle> instance is recreated and
      // its DOM re-syncs to branch.status (which didn't change). Without this,
      // the native checkbox stays in the optimistically-flipped state until
      // the page reloads.
      toggleVersions: {},
      // Per-branch in-flight flags for status API calls. Drives the row dim
      // + spinner so the user gets visible feedback during the network round-trip.
      updatingStatuses: {},
      page: 1,
      perPage: 12,
      searchQuery: "",
      statusFilter: "active",
      statusOptions: [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
        { value: "deleted", label: "Deleted" },
      ],
    };
  },
  computed: {
    allUsers() {
      return this.$store.getters["subadmin/users"];
    },
    filteredUsers() {
      let filtered = this.allUsers;

      // Apply search filter — match across multiple fields
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        const matches = (v) => v != null && String(v).toLowerCase().includes(q);
        filtered = filtered.filter(user =>
          matches(user.username) ||
          matches(user.name) ||
          matches(user.email) ||
          matches(user.phone) ||
          matches(user.upiid) ||
          matches(user.upiId)
        );
      }

      // Apply status filter
      if (this.statusFilter === "active") {
        filtered = filtered.filter(user => user.status === 1 && user.is_deleted === 0);
      } else if (this.statusFilter === "inactive") {
        filtered = filtered.filter(user => user.status === 0 && user.is_deleted === 0);
      } else if (this.statusFilter === "deleted") {
        filtered = filtered.filter(user => user.is_deleted === 1);
      } else {
        // "all" - show only non-deleted by default
        filtered = filtered.filter(user => user.is_deleted === 0);
      }

      // Sort: active first, then inactive, then deleted
      return [...filtered].sort((a, b) => {
        const rank = (u) => (u.is_deleted === 1 ? 2 : u.status === 1 ? 0 : 1);
        return rank(a) - rank(b);
      });
    },
    hasUsers() {
      return this.$store.getters["subadmin/hasUsers"];
    },
    hasFilteredUsers() {
      return this.filteredUsers.length > 0;
    },
    pagedUsers() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredUsers.slice(start, start + this.perPage);
    },
    paginatorInfo() {
      const total = this.filteredUsers.length;
      const lastPage = Math.ceil(total / this.perPage) || 1;
      const firstItem = total === 0 ? 0 : (this.page - 1) * this.perPage + 1;
      const lastItem = Math.min(this.page * this.perPage, total);
      return {
        total,
        currentPage: this.page,
        lastPage,
        firstItem,
        lastItem,
        hasMorePages: this.page < lastPage,
        perPage: this.perPage,
      };
    },
    getFormText() {
      return this.mode === "create" ? "Create Branch" : `Update Branch - ${this.selectedUser.username}`;
    },
  },
  watch: {
    statsDate() { this.loadStats(); },
    searchQuery() { this.page = 1; },
    statusFilter() { this.page = 1; },
  },
  created() {
    this.getUsers();
  },
  methods: {
    can(perm) { return this.$store.getters.hasPermission(perm); },
    setPage(page) { this.page = page; },
    // Per-direction routing exclusivity label: 'PAY-IN' / 'PAY-OUT' /
    // 'BOTH', or null when fully open. Falls back to the legacy
    // direction-blind branch_serves_default for rows served before the
    // 22072026 migration.
    branchExclusiveLabel(b) {
      const legacy = b.branch_serves_default != null && Number(b.branch_serves_default) === 0;
      const payin = b.branch_serves_default_payin != null
        ? Number(b.branch_serves_default_payin) === 0
        : legacy;
      const payout = b.branch_serves_default_payout != null
        ? Number(b.branch_serves_default_payout) === 0
        : legacy;
      if (payin && payout) return 'BOTH';
      if (payin) return 'PAY-IN';
      if (payout) return 'PAY-OUT';
      return null;
    },
    initialsOf(u) {
      const s = (u.name || u.username || "?").trim();
      const parts = s.split(/\s+/);
      const a = parts[0]?.[0] || "?";
      const b = parts[1]?.[0] || "";
      return (a + b).toUpperCase().slice(0, 2);
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    formatPercent(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      const trimmed = n % 1 === 0 ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
      return trimmed + "%";
    },
    hasStats(branch) {
      const s = this.branchStats[branch.username];
      const hasActivity = s && (
        Number(s.payInApprovedCount) > 0 ||
        Number(s.payOutApprovedCount) > 0 ||
        Number(s.payInApprovedAmount) > 0 ||
        Number(s.payOutApprovedAmount) > 0
      );
      return !!hasActivity || Number(branch.payInLimit) > 0;
    },
    getStat(branch, key) {
      const s = this.branchStats[branch.username];
      return s ? s[key] : 0;
    },
    async getUsers() {
      this.isLoading = true;
      try {
        await this.$store.dispatch("subadmin/getUsers");
        await this.loadStats();
      } catch (error) {
        if (!error.status) this.isPermission = false;
        this.error = error.message || "Something failed!";
      }
      this.isLoading = false;
    },
    async loadStats() {
      this.isLoadingStats = true;
      try {
        const response = await http.get(`/dashboard/list-stats?type=branch&date=${this.statsDate}`);
        if (response.data.success) {
          const map = {};
          response.data.data.forEach(item => { map[item.username] = item; });
          this.branchStats = map;
        }
      } catch (e) {
        console.error("Error loading branch stats:", e);
      }
      this.isLoadingStats = false;
    },
    handleError() { this.error = null; },
    handleBaseToast() { this.baseToast = { type: "", message: "" }; },
    createBtnClicked() {
      this.mode = "create";
      this.isCreateUser = true;
      this.selectedUser = Object;
    },
    editUser(user) {
      if (!this.can("branch.edit")) return;
      this.mode = "update";
      this.isCreateUser = true;
      this.selectedUser = user;
    },
    openBranchReport(branch) {
      this.selectedBranchUsername = branch.username;
      this.openBranchStatistics = true;
    },
    closeBranchReport() {
      this.openBranchStatistics = false;
      this.selectedBranchUsername = "";
    },
    async createUser(data) {
      this.isLoading = true;
      try {
        if (this.mode === "create") {
          await this.$store.dispatch("subadmin/createUser", data);
          this.baseToast.message = "Branch created successfully!";
        } else {
          await this.$store.dispatch("subadmin/updateUser", data);
          this.baseToast.message = "Branch updated successfully!";
        }
        // Re-fetch so the list reflects exactly what persisted server-side.
        // The optimistic local merge covers the common fields, but a
        // refetch is the durable guarantee that display == backend (no
        // stale toggles, no false "changed").
        await this.$store.dispatch("subadmin/getUsers");
        this.isCreateUser = false;
        this.baseToast.type = "success";
        setTimeout(() => { this.handleBaseToast(); }, 1000);
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
      this.isLoading = false;
    },
    async deleteUser(user) {
      this.selectedUser = user;
      this.$swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this! Pending orders from banks under this branch will be reassigned to you.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.$store.dispatch("subadmin/deleteUser", this.selectedUser);
          this.$swal.fire("Deleted!", "Branch has been deleted.", "success");
          this.selectedUser = Object;
          this.isCreateUser = false;
        }
      });
    },
    async toggleStatus(branch) {
      if (this.updatingStatuses[branch.username]) return;
      const newStatus = branch.status === 1 ? 0 : 1;
      const action = newStatus === 0 ? "deactivate" : "activate";
      const warningText = newStatus === 0
        ? "Deactivating this branch will reassign all pending orders from its banks to you."
        : "This will activate the branch.";

      this.$swal.fire({
        title: `Are you sure you want to ${action} this branch?`,
        text: warningText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: newStatus === 0 ? "#d33" : "#3085d6",
        cancelButtonColor: "#6b7280",
        confirmButtonText: `Yes, ${action} it!`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.setUpdating(branch.username, true);
          try {
            await this.$store.dispatch("subadmin/changeUserStatus", {
              username: branch.username,
              status: newStatus,
            });
            this.baseToast.type = "success";
            this.baseToast.message = `Branch ${action}d successfully!`;
            setTimeout(() => { this.handleBaseToast(); }, 2000);
          } catch (error) {
            this.error = error.message || "Something failed!";
            this.bumpToggleVersion(branch.username);
          } finally {
            this.setUpdating(branch.username, false);
          }
        } else {
          this.bumpToggleVersion(branch.username);
        }
      });
    },
    setUpdating(username, value) {
      if (value) {
        this.updatingStatuses = { ...this.updatingStatuses, [username]: true };
      } else {
        const next = { ...this.updatingStatuses };
        delete next[username];
        this.updatingStatuses = next;
      }
    },
    bumpToggleVersion(username) {
      // Reassign the object so Vue's reactivity catches the change for keys
      // not previously present.
      this.toggleVersions = {
        ...this.toggleVersions,
        [username]: (this.toggleVersions[username] || 0) + 1,
      };
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.br-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}
.list-view, .form-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

/* ==================== Header ==================== */
.br-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.br-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
}

/* Form head (breadcrumb inline) */
.form-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 4px;
  flex-wrap: wrap;
  min-height: 32px;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  border: none;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.12s, background 0.12s;
}
.back-link:disabled { opacity: 0.5; cursor: not-allowed; }
.back-link:hover:not(:disabled) { color: #1B2A47; background: #eef2f7; }
.back-link svg { width: 16px; height: 16px; }

.head-sep { color: #cbd5e1; display: inline-flex; align-items: center; }
.head-sep svg { width: 14px; height: 14px; }

.head-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.head-chip-new {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 4px 10px -4px rgba(27, 42, 71, 0.45);
}
.head-chip-edit {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.head-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.head-name-sub {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0;
  text-transform: none;
}

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  text-decoration: none;
  font-family: inherit;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }

/* ==================== Filters ==================== */
.br-filters {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  color: #0f172a;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s;
}
.search-input:focus { border-color: #1B2A47; background: #fff; }
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

.chip-group { display: flex; gap: 4px; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  font-family: inherit;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}

/* ==================== Loading + empty ==================== */
.br-loading {
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

.loading-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 60px 20px;
}

.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}
.empty-hero-icon {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 28px; height: 28px; }
.empty-hero-title { font-size: 15px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 13px; color: #64748b; }

/* ==================== Cards ==================== */
.br-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}
.br-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  position: relative;
}
@media (min-width: 1200px) { .br-grid { grid-template-columns: 1fr 1fr; } }

.branch-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.branch-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 8px 22px -10px rgba(27, 42, 71, 0.22);
  transform: translateY(-1px);
}

.card-top {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
}
.avatar {
  width: 42px; height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1B2A47, #2C3E63);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.branch-meta { min-width: 0; }
.branch-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.branch-sub {
  font-size: 12px;
  color: #64748b;
  display: flex; gap: 6px; align-items: center;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.dot-sep { color: #cbd5e1; }
.branch-email { color: #475569; font-weight: 500; }

.card-badges { display: flex; gap: 6px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; }
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.pill-success { background: #d1fae5; color: #065f46; }
.pill-neutral { background: #f1f5f9; color: #64748b; }
.pill-danger  { background: #fee2e2; color: #991b1b; }

/* Stats */
.card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}
.card-stats-loading .skeleton {
  height: 46px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 400% 100%;
  border-radius: 8px;
  animation: shine 1.4s ease-in-out infinite;
}
@keyframes shine {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.stat-cell {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 8px 10px;
}
.stat-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
}
.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}
.stat-mono { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 13px; }
.stat-slash { color: #cbd5e1; margin: 0 2px; }
.stat-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}
@media (max-width: 720px) {
  .card-stats { grid-template-columns: 1fr 1fr; }
  .card-stats > .stat-cell:nth-child(3) { grid-column: 1 / -1; }
}

/* Card actions */
.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  color: #475569;
  transition: all 0.12s;
}
.action-btn svg { width: 14px; height: 14px; }
.action-btn:hover { border-color: #cbd5e1; background: #f8fafc; }
.action-report:hover { color: #059669; background: #ecfdf5; border-color: #a7f3d0; }
.action-edit:hover   { color: #1B2A47; background: #eef2f7; border-color: #cbd5e1; }
.action-delete       { color: #b91c1c; }
.action-delete:hover { background: #fef2f2; border-color: #fecaca; color: #991b1b; }

.action-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  transition: opacity 0.2s;
}
.action-toggle-updating {
  opacity: 0.6;
  pointer-events: none;
}
.toggle-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.mini-spinner {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.7s linear infinite;
}

.pagination-wrap {
  margin-top: auto;
  padding-top: 8px;
}

/* ==================== Permission fallback ==================== */
.permission-block {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}
.permission-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 40px 32px;
  max-width: 420px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 30px -20px rgba(15, 23, 42, 0.25);
}
.permission-icon {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: #fef2f2;
  color: #dc2626;
  display: flex; align-items: center; justify-content: center;
}
.permission-icon svg { width: 28px; height: 28px; }
.permission-card h2 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
}
.permission-card p {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 8px;
}

/* ==================== Toast ==================== */
.br-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #0f172a; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(15, 23, 42, 0.4);
}
.br-toast .dot { width: 8px; height: 8px; border-radius: 999px; background: #94a3b8; }
.br-toast.is-success { background: #059669; }
.br-toast.is-success .dot { background: #a7f3d0; }
.br-toast.is-error { background: #dc2626; }
.br-toast.is-error .dot { background: #fecaca; }

.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.24s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Branch list transition (preserved from original) */
:deep(.branch-list-move),
:deep(.branch-list-enter-active),
:deep(.branch-list-leave-active) {
  transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
:deep(.branch-list-enter-from) { opacity: 0; transform: translateY(-12px); }
:deep(.branch-list-leave-to) { opacity: 0; transform: translateX(40px) scale(0.97); }
:deep(.branch-list-leave-active) { position: absolute; left: 0; right: 0; z-index: 0; }

/* ==================== Report modal (teleported) ==================== */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.modal-card-wide { max-width: 840px; }
.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.modal-sub { font-size: 12.5px; color: #64748b; }
.modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.modal-close svg { width: 16px; height: 16px; }

.modal-body {
  padding: 20px 22px;
}
.modal-body-scroll { overflow-y: auto; max-height: calc(100vh - 200px); }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .modal-card { transform: translateY(-8px) scale(0.98); }
</style>
